import { IEvent, IOrg, IOrgInvite, IOrgPerson, SuggestedOrgInviteReason } from "@schema";
import { AugmentedPersonMap } from "./OnboardingOrgInvite";
import { compareBooleans, compareStrings } from "@util/sort.util";
import { getValue } from "@util/errorable.util";

type GetAugmentedPersonArgs = {
  org: IOrg;
  showCurrentUser: boolean;
  query: string;
};

type PersonForInviteList = Pick<IOrgPerson, "primaryCalendarId"> &
  Partial<Pick<IOrgPerson, "isYou" | "userId" | "profile">>;

export const getPendingInviteMap = (org: IOrg): { [calId: string]: IOrgInvite } => {
  const pendingInvites = org.pendingInvites;
  const pendingInviteMap: { [calId: string]: IOrgInvite } = {};
  if (!pendingInvites || !pendingInvites.edges) {
    return pendingInviteMap;
  }

  pendingInvites.edges.forEach((edge: any) => {
    pendingInviteMap[edge.node.targetCalendarId] = edge.node;
  });

  return pendingInviteMap;
};

export const sortPersons = (persons: AugmentedPersonMap) => {
  return [...Object.keys(persons)].sort((a, b) => {
    const personA = persons[a];
    const personB = persons[b];

    return (
      compareBooleans(personA.isYou, personB.isYou) || // you go to the bottom
      compareBooleans(personA.isUser, personB.isUser) || // users go to the bottom
      compareBooleans(personA.isPending, personB.isPending) ||
      (personA.isSuggested && personB.isSuggested && personB.totalScore - personA.totalScore) || // best suggestions to the top
      compareBooleans(personA.isSuggested, personB.isSuggested, false) || // suggestions to the top (note isAscending = false)
      compareStrings(personA.givenName, personB.givenName) ||
      compareStrings(personA.familyName, personB.familyName)
    );
  });
};

export const isAtLeastOnePersonNotSelectedAndNotUser = (
  currentPersons: AugmentedPersonMap,
  selectedCalendarIds: string[],
) => {
  const persons = currentPersons;
  return sortPersons(persons)
    .slice(0, 20) // for perf reasons, only render 10
    .some(
      (calId: any) =>
        !persons[calId].isUser &&
        !persons[calId].isPending &&
        selectedCalendarIds.indexOf(calId) === -1,
    );
};

export const getAugmentedPersons = ({ org, showCurrentUser, query }: GetAugmentedPersonArgs) => {
  const pendingInviteMap = getPendingInviteMap(org);

  // sanity check
  if (!org || !org.suggestedInvites || !org.suggestedInvites.edges) {
    return {} as AugmentedPersonMap;
  }

  let persons: PersonForInviteList[] = [];
  const personList = getValue(org.orgPersonListPaginatedErrorable);
  if (!personList || !personList?.searchPersons || !personList?.searchPersons?.length) {
    return {} as AugmentedPersonMap;
  }
  persons = personList.searchPersons.map((p) => p.person);

  // if a teammate leaves the org, their person won't return
  // check current teammates for anybody missing and create shim persons for them

  const result: AugmentedPersonMap = persons
    .filter((person) => {
      if (!showCurrentUser && person?.isYou) {
        return false;
      }
      return true;
    })
    .reduce((accumulator: AugmentedPersonMap, nextPerson): AugmentedPersonMap => {
      const { profile, primaryCalendarId, isYou, userId } = nextPerson;
      const isPending = !!pendingInviteMap[primaryCalendarId];

      // defaults
      accumulator[primaryCalendarId] = {
        isYou: Boolean(isYou),
        isUser: Boolean(userId),
        isPending,
        targetCalendarId: primaryCalendarId,
        givenName: profile ? profile.givenName : primaryCalendarId, // if no user profile, show primaryEmail
        familyName: profile ? profile.familyName : "",
        externalImageUrl: profile ? profile.externalImageUrl : "",
        isSuggested: false,
        numberOfSharedMeetings: 0,
        numberOfOrganizedMeetings: 0,
        inviteReason: SuggestedOrgInviteReason.AttendSameMeetings,
        totalScore: 0,
      };

      return accumulator;
    }, {});

  // Append the suggested persons to the result. if the user is typing in the search, we dont append these
  if (org?.suggestedInvites?.edges?.length && !query) {
    for (let suggestedPerson of org?.suggestedInvites?.edges) {
      const node = suggestedPerson.node;
      const {
        targetCalendarId,
        inviteReason,
        numberOfSharedMeetings,
        numberOfOrganizedMeetings,
        totalScore,
        targetProfile,
      } = node;
      const isPending = !!pendingInviteMap[targetCalendarId];

      // Add the suggested persons in the result object, or replace them with the correct data
      result[targetCalendarId] = {
        isYou: false,
        isUser: false,
        isPending,
        targetCalendarId,
        givenName: targetProfile ? targetProfile.givenName : targetCalendarId, // if no user profile, show primaryEmail
        familyName: targetProfile ? targetProfile.familyName : "",
        externalImageUrl: targetProfile ? targetProfile.externalImageUrl : "",
        isSuggested: true,
        numberOfSharedMeetings,
        numberOfOrganizedMeetings,
        inviteReason,
        totalScore,
      };
    }
  }

  return result;
};

export const getSuggestedEvents = (org: IOrg, events: IEvent[]) => {
  // Filter out any events that are: 1) not repeating, 2) over 10 attendees, 3) no 1:1s
  const eventsThatHaveIntervalsAndMultipleAttendees = events.filter((event: IEvent) => {
    const attendees = getValue(event.attendeePeople);
    if (
      event.recurrenceRules &&
      attendees &&
      attendees?.list?.length > 2 &&
      attendees?.list?.length < 11 // 11 including current user, in reality we want 10 others to invite max
    ) {
      return event;
    } else {
      return null;
    }
  });

  const pendingPersonsMap = getPendingInviteMap(org);
  const eventsList: {
    [keyof: string]: AugmentedPersonMap;
  } = {};

  for (let event of eventsThatHaveIntervalsAndMultipleAttendees) {
    const attendeesThatAreNotUsers: AugmentedPersonMap = {};
    const attendees = getValue(event.attendeePeople);
    if (attendees && attendees?.list?.length) {
      for (let attendee of attendees?.list) {
        const isPending = !!pendingPersonsMap[attendee.primaryEmail];
        const { primaryEmail, profile, isUser, isYou } = attendee;
        if (!isYou && !isUser && !isPending) {
          attendeesThatAreNotUsers[primaryEmail] = {
            isYou: Boolean(isYou),
            isUser: isUser,
            isPending: isPending,
            targetCalendarId: attendee.primaryEmail,
            givenName: profile ? profile.givenName : primaryEmail, // if no user profile, show primaryEmail
            familyName: profile?.familyName || "",
            externalImageUrl: profile?.externalImageUrl || "",
            isSuggested: true,
            numberOfSharedMeetings: 0,
            numberOfOrganizedMeetings: 0,
            inviteReason: SuggestedOrgInviteReason.AttendSameMeetings,
            totalScore: 0,
          };
        }
      }
    }
    // Check again for multiple attendees (since we filtered out a lot) and if we've already grabbed 2 events
    if (Object.keys(attendeesThatAreNotUsers).length > 1 && Object.keys(eventsList).length < 2) {
      eventsList[event.title] = attendeesThatAreNotUsers;
    }
  }
  return eventsList;
};
